import { useRef } from "react";
import { startOfTomorrow } from "date-fns";

import { useFormState } from "common/hooks";
import { FrequencyUnit } from "common/types";
import { FetchError } from "common/fetch/types";
import { toClinicDate } from "common/utils/dates";
import { cleanNumberInput } from "common/utils/numbers";
import { parseEnum } from "common/utils/strings";
import { Optional } from "common/utils/types";

import CalendarAccordion from "client/common/components/calendars/CalendarAccordion";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import Select from "client/common/components/inputs/Select";
import TextInput from "client/common/components/inputs/TextInput";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import {
  DosageReminder,
  DosageReminderItem,
  DosageReminderPatient,
  DosageReminderUpdate,
  IsDeliveryMethodSelected,
  ReminderFormValues,
} from "../types";
import { INTERVAL_LABELS, SINGULAR_INTERVAL_LABELS } from "../constants";
import { useDeliveryMethodSelection } from "../hooks";
import { useReminderItemSelectDrawer } from "../stackables";
import DeliveryMethodCard from "./DeliveryMethodCard";
import ItemSummary from "./ItemSummary";
import ReminderDrawerLayout from "./ReminderDrawerLayout";

import styles from "../styles/ReminderDrawerForm.module.less";

type ReminderFormError = FetchError<
  Pick<DosageReminderUpdate, "frequency" | "next_dose_date" | "email_enabled" | "text_enabled">
>;

const getBlankValues = (): ReminderFormValues => ({
  isActive: true,
  nextDoseDate: null,
  repeatEvery: "",
  intervalUnit: FrequencyUnit.Month,
  stopDate: null,
  deliveryMethods: {
    text: true,
    email: false,
  },
});

const getInitialValues = (reminder: DosageReminder): ReminderFormValues => ({
  isActive: reminder.is_active,
  nextDoseDate: reminder.next_dose_date ? toClinicDate(reminder.next_dose_date) : startOfTomorrow(),
  repeatEvery: String(reminder.frequency),
  intervalUnit: reminder.interval_unit,
  stopDate: reminder.stop_date ? toClinicDate(reminder.stop_date) : null,
  deliveryMethods: {
    text: reminder.text_enabled,
    email: reminder.email_enabled,
  },
});

/** Text and email errors share one card, so duplicate messages are shown once */
const getDeliveryMethodErrors = (submitError: Optional<ReminderFormError>) => [
  ...new Set([
    ...(submitError?.errors.text_enabled ?? []),
    ...(submitError?.errors.email_enabled ?? []),
  ]),
];

type ReminderDrawerFormProps = {
  patient: DosageReminderPatient;
  item: DosageReminderItem;
  /** The reminder being edited, or null when creating one */
  reminder: DosageReminder | null;
  isSubmitting: boolean;
  submitError: Optional<ReminderFormError>;
  onSubmit: (
    values: ReminderFormValues,
    isDeliveryMethodSelected: IsDeliveryMethodSelected,
  ) => void;
  /** Lets the user go back to the item select drawer to pick a different item */
  canGoBack: boolean;
};

const ReminderDrawerForm = ({
  patient,
  item,
  reminder,
  isSubmitting,
  submitError,
  onSubmit,
  canGoBack,
}: ReminderDrawerFormProps) => {
  const { close } = useCurrentDrawer();
  const reminderItemSelectDrawer = useReminderItemSelectDrawer();
  const repeatEveryRef = useRef<HTMLInputElement>(null);

  const [values, updateField, setValues] = useFormState(() =>
    reminder ? getInitialValues(reminder) : getBlankValues(),
  );
  const { isActive, nextDoseDate, repeatEvery, intervalUnit, stopDate, deliveryMethods } = values;

  const { isSelected: isDeliveryMethodSelected, isLoading: isLoadingDeliveryMethods } =
    useDeliveryMethodSelection(deliveryMethods);

  const isSingleInterval = Number(repeatEvery) === 1;

  const handleNextDoseDateChange = (date: Date) => {
    setValues((currentValues) => ({
      ...currentValues,
      nextDoseDate: date,
      ...(currentValues.stopDate && currentValues.stopDate < date && { stopDate: date }),
    }));
  };

  const handleActiveChange = (isChecked: boolean) => {
    updateField("isActive")(isChecked);

    if (isChecked && !reminder?.is_active) {
      handleNextDoseDateChange(
        nextDoseDate && nextDoseDate >= startOfTomorrow() ? nextDoseDate : startOfTomorrow(),
      );
    }
  };

  const handleBack = () => {
    close();
    reminderItemSelectDrawer().open();
  };

  const handleRepeatEveryChange = (value: string) => {
    const cleanedValue = cleanNumberInput(value);
    updateField("repeatEvery")(cleanedValue);
    // Dismisses the mobile keyboard after each digit, but not when deleting
    if (cleanedValue.length > repeatEvery.length) {
      repeatEveryRef.current?.blur();
    }
  };

  return (
    <ReminderDrawerLayout
      onBack={canGoBack ? handleBack : null}
      actions={
        <DrawerLayout.Actions.Button
          disabled={isLoadingDeliveryMethods || isSubmitting}
          isLoading={isSubmitting}
          onClick={() => onSubmit(values, isDeliveryMethodSelected)}
        >
          {reminder ? "Save changes" : "Create reminder"}
        </DrawerLayout.Actions.Button>
      }
    >
      {submitError && <FetchErrorAlert error={submitError} displayAllErrors={true} />}
      <ItemSummary
        patient={patient}
        item={item}
        isActive={isActive}
        onActiveChange={reminder ? handleActiveChange : null}
      />
      <div className={styles.schedule}>
        <CalendarAccordion
          label="Next dose date"
          date={nextDoseDate}
          onChange={handleNextDoseDateChange}
          minDate={startOfTomorrow()}
          placeholder="Select a date"
          disabled={!isActive}
          errors={submitError?.errors.next_dose_date}
        />
        <div className={styles.repeatRow}>
          <TextInput
            label="Repeat every"
            inputMode="numeric"
            ref={repeatEveryRef}
            value={repeatEvery}
            disabled={!isActive}
            errors={submitError?.errors.frequency}
            onChange={handleRepeatEveryChange}
          />
          <Select
            label="Interval"
            value={intervalUnit}
            disabled={!isActive}
            onChange={(value) =>
              updateField("intervalUnit")(parseEnum(value, FrequencyUnit, intervalUnit))
            }
          >
            {Object.values(FrequencyUnit).map((unit) => (
              <Select.Option key={unit} value={unit}>
                {isSingleInterval ? SINGULAR_INTERVAL_LABELS[unit] : INTERVAL_LABELS[unit]}
              </Select.Option>
            ))}
          </Select>
        </div>
        <CalendarAccordion
          label="Stop date"
          date={stopDate}
          onChange={updateField("stopDate")}
          onClear={() => updateField("stopDate")(null)}
          clearLabel="Remove stop date"
          minDate={nextDoseDate ?? startOfTomorrow()}
          placeholder="No stop date"
          disabled={!isActive}
        />
        <DeliveryMethodCard
          value={deliveryMethods}
          onChange={updateField("deliveryMethods")}
          errors={getDeliveryMethodErrors(submitError)}
          disabled={!isActive}
        />
      </div>
    </ReminderDrawerLayout>
  );
};

export default ReminderDrawerForm;
